import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
import FolderView from "@/components/FolderView";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { Deck } from "@/types/linguadeck";
import { useAuth } from "@/contexts/AuthContext";
import { fetchSharedFolderWithCards, buildFolderStudyCards } from "@/lib/db";

export default function SharedFolderPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sharedDecks, setSharedDecks] = useState<Deck[]>([]);
  const publicHomeHref = user ? "/" : "/landing";

  const sharedDeckTokens = useMemo(
    () =>
      Object.fromEntries(
        sharedDecks
          .filter((deck) => typeof deck.shareToken === "string" && deck.shareToken.trim() !== "")
          .map((deck) => [deck.id, deck.shareToken as string])
      ) as Record<string, string>,
    [sharedDecks]
  );

  const openSharedDeck = (deckId: string, mode?: "cards" | "learn" | "test" | "games") => {
    const deckToken = sharedDeckTokens[deckId];
    if (!deckToken) return;
    const search = mode && mode !== "cards" ? `?mode=${mode}` : "";
    navigate(`/share/${deckToken}${search}`);
  };

  const handleStudyFolder = useCallback(
    async (_folderId?: string, _deckIds?: string[], _folderTitle?: string, mode?: "cards" | "learn" | "test" | "games") => {
      if (!token) return;
      try {
        const result = await fetchSharedFolderWithCards(token);
        if (!result || result.decks.length === 0) {
          toast.error(t("folder.noCardsToStudy"));
          return;
        }
        const cards = buildFolderStudyCards(result.decks);
        if (cards.length === 0) {
          toast.error(t("folder.noCardsToStudy"));
          return;
        }
        const firstDeck = result.decks[0];
        const virtualDeck: Deck = {
          id: `folder-${result.folder.id}`,
          title: result.folder.title,
          lang: firstDeck.lang ?? "en",
          emoji: firstDeck.emoji ?? "📁",
          cards,
        };
        const search = mode && mode !== "cards" ? `?mode=${mode}` : "";
        navigate(`/share/folder/${token}/study${search}`, { state: { deck: virtualDeck, folderTitle: result.folder.title } });
      } catch (err) {
        console.error("Failed to load folder for study:", err);
        toast.error(t("folder.addMaterialsError"));
      }
    },
    [token, navigate, t]
  );

  return (
    <div
      className="min-h-[100svh] bg-[hsl(var(--bg))] text-[hsl(var(--foreground))]"
      style={{ minHeight: "100dvh", fontFamily: "DM Sans, sans-serif" }}
    >
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--bg2))]/95 pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to={publicHomeHref} className="flex min-w-0 items-center gap-3 no-underline">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent2))] text-white">
              <BookOpen className="h-5 w-5" />
            </div>
            <span className="truncate text-lg font-bold text-[hsl(var(--foreground))]">StudyDo</span>
          </Link>

          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <ThemeToggle compact />
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-4 sm:px-6 md:py-6">
        {/* sharedToken present: decks prop is ignored; FolderView loads data via onDecksLoaded */}
        <FolderView
          sharedToken={token ?? "__missing_folder_token__"}
          decks={[]}
          folders={[]}
          onSelectDeck={openSharedDeck}
          onStudyDirect={openSharedDeck}
          onStudyFolder={handleStudyFolder}
          onNavTo={() => {}}
          onDecksLoaded={setSharedDecks}
        />
      </div>
    </div>
  );
}
